import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { FigureIntervalResidual } from '@/components/Patterns';
import { PageMasthead } from '@/components/ui/PageMasthead';
import { PageNav } from '@/components/ui/PageNav';
import { MobileSectionNav } from '@/components/ui/MobileSectionNav';
import { useSectionSpy } from '@/hooks/useSectionSpy';

const ATLAS_DOMAINS = [
  {
    num: 'I',
    title: 'Demand & Forecast Structure',
    subtitle: 'Structural Demand Instability Patterns',
    intro: [
      'The answer to "What causes instability?" is rarely a wrong total.',
      'The reason that most enterprise environments fail is not because monthly volume was mispredicted but rather because demand shape, residual direction and volatility exposure were not understood.',
      'This domain isolates structural distortions embedded inside forecasting logic and demand modeling assumptions.',
      'The question is not whether the forecasts are “correct” in aggregate, but whether they fit at the interval level.',
    ],
    patterns: [
      {
        num: 1,
        name: 'Systematic Directional Forecast Bias',
        scenario: 'A 250 FTE multiqueue inbound operation has reported forecast accuracy consistently above 94 percent. Total MAPE remains within allowable planning tolerance. However, we observed that service instability manifested on some particular weekday. No serious deviation appears in the executive dashboard and planning teams classify performance dips as normal variance. Interval-level analysis uncovers something else. The residuals always skew in the same direction on certain days of the week.',
        signals: ['The residuals have the same sign on comparable days of the week.', 'Mean residual per day is clearly non zero.', 'The variance underestimation is concentrated in the same time slots.', 'SLA Degradation is linked to residual direction', 'Recovery lag repeats when negative bias occurs'],
        signalNote: 'This is structural directional forecast bias, not a random error.',
        drivers: ['Smoothing through structurally different days', 'Recalculation cadence too slow relative to demand drift', 'Overweighting of central tendency', 'Promotion and/or behavioral drivers not segregated appropriately', 'Interaction between forecast bias and rigid coverage architecture', 'Bias monitoring not done at an interval level'],
        driverNote: 'The distortion is embedded in modeling assumptions, not in frontline execution.',
        inputs: ['Six to ten weeks of interval forecast versus actual', 'Interval arrival logs', 'Interval coverage distribution', 'SLA and ASA logs', 'Optional AHT variance'],
        inputNote: 'All data available through normal WFM exports.',
        method: ['Normalize residuals across intervals', 'Stratify by day of the week', 'Calculate directional bias coefficient', 'Model variance band expansion', 'Map exposure weighted SLA sensitivity', 'Performance for the case of corrected residual misalignment should be simulated', 'Stresstest against current staffing distribution'],
        methodNote: 'Aim is to analyze structural bias separate from variability of execution.',
        artifacts: 'Forecast Residual Decomposition Report',
        artifactItems: ['Bias index by day of the week', 'Residual variance envelope', 'All exposure adjusted instability heatmaps', 'Bias amplification gradient', 'Structural correction guidance'],
        engagement: 'The diagnostic session clarifies whether instability originates from:\n\n• Assumptions in forecast modeling\n• How often should recalibration be carried out\n• Or the interaction with schedule inflexibility\n\nTo gain a better understanding of if performance friction is due to model or execution.',
      },
      {
        num: 2,
        name: 'Intraday Arrival Density Compression',
        scenario: 'An operation of 180 to 300 FTEs has daily forecast totals that are stable and weekly accuracy that is reasonable. But SLA fails again during the respective intervals. Daily staffing aligns with total forecast and OPS characterize repeating burn intervals with no visible macro spikes.',
        signals: ['15 minute arrival clustering orders beyond the expected stochastic distribution', 'Density peaks are above the predicted resolution', 'Hourly aggregation seems to be steady whereas stress at interval level has increased', 'Stress windows repeat at approximately the same time', 'SLA sensitivity is especially high during density spikes'],
        signalNote: 'This indicates a structural compression of demand shape and is not a macro volume error.',
        drivers: ['Insufficient forecast granularity vs volatility.', 'Aggregation smoothing masking interval clustering', 'Shift blocks misaligned with density peaks', 'No density weighted coverage allocation', 'Threshold sensitivity not wired into staffing logic', 'Intra hour volatility isn’t captured.'],
        driverNote: 'The issue is shape, not total volume.',
        inputs: ['15-30 minute arrival distribution', 'Forecast interval output', 'Schedule coverage by interval', 'SLA and queue depth logs', 'Optional AHT distribution'],
        inputNote: 'All export based.',
        method: ['Reshape empirical arrival density curve', 'Determine compression coefficients relative to the forecast’s resolution', 'Model coverage elasticity under density stress', 'Verify compression interval by repetition', 'Map SLA sensitivity to density gradient', 'Simulate redistribution without altering headcount'],
        methodNote: 'This isolates structural shape distortion from staffing sufficiency.',
        artifacts: 'Interval Density & Coverage Elasticity Analysis',
        artifactItems: ['Compression intensity index', 'Coverage misalignment heatmap', 'Structural fragility interval map', 'Elasticity stress curve', 'Redistribution impact projection'],
        engagement: 'Diagnostic session determines whether instability is driven by:\n\n• Forecast resolution limits\n• Coverage rigidity\n• Or threshold sensitivity\n\nEnables structural correction without defaulting to headcount increase.',
      },
      {
        num: 3,
        name: 'Tail Volatility Exposure',
        scenario: 'Monthly forecasts show a persistent accuracy of over 92 percent. Rare high impact days produce SLA collapse. Recovery lag is longer than the anticipated stabilization window. Buffer policy at plus fifteen percent. Extreme days labeled as exceptions. Distribution analysis suggests the presence of a structural tail exposure.',
        signals: ['Upper heavy tail in arrival distribution percentile', 'SLA breach probability with a sharp rise at very high percentiles.', 'Buffer exhaustion occurs at roughly the same bounds', 'Recovery slope asymmetry', 'Tail variance underestimation in forecast modeling'],
        signalNote: 'That\u2019s not a failing operation but volatility mispricing.',
        drivers: ['Planning anchored on mean demand', 'Static percentage based buffer logic', 'No percentile weighted staffing modifications', 'Rigid shift plan', 'No volatility stress modeling', 'Overconfidence in average based resilience'],
        driverNote: 'Assumed distribution is different from empirical distribution.',
        inputs: ['Eight to twelve weeks of arrival distribution', 'Forecast residual history', 'Staffing buffer policy', 'Interval level SLA logs', 'Optional shrinkage and AHT variance'],
        inputNote: 'All exportable.',
        method: ['Model percentile distribution of arrival curve', 'Quantify the tail probability', 'Stress-test buffer suitability', 'Map exposure weighted SLA risk', 'Model volatility elasticity', 'Derive resilience thresholds'],
        methodNote: 'This removes structural exposure outside the average case.',
        artifacts: 'Volatility Exposure & Structural Resilience Report',
        artifactItems: ['Tail percentile exposure profile', 'Buffer adequacy ratio', 'Structural resilience index', 'SLA breach probability curve', 'Risk adjusted staffing sensitivity band'],
        engagement: 'Clarifies whether instability is driven by structural volatility exposure beyond buffer assumptions.\n\nSupports risk framing based on percentile modeling rather than averages.',
      },
    ],
  },
  {
    num: 'II',
    title: 'Coverage & Schedule Architecture',
    subtitle: 'Structural Distribution & Staffing Design Distortions',
    intro: [
      'Coverage instability is rarely about insufficient headcount.',
      'In enterprise environments total staffing often matches the forecasted requirement.',
      'Instability seems to happen when distribution is not the same as demand shape.',
      'This domain isolates structural distortions embedded in shift design, overlap logic and skill allocation architecture.',
      'The question is not how many agents exist. The relevant question is when and where they are located along the demand pressure.',
    ],
    patterns: [
      {
        num: 4,
        name: 'Coverage Compression Misalignment',
        scenario: 'An inbound operation with 220 FTE achieves targeted staffing levels and total planned hours. Headcount meets requirement and utilization report looks ok. However, SLA instability still occurs for intervals of high density. Staffing seems to be sufficient to management. Distribution of staffing is not adequate from the perspective of interval data.',
        signals: ['In periods of low load: over staffing', 'Understaffing during peak intervals', 'The covering curve and arriving curve are wrongly arranged', 'SLA fragility concentrated in compression zones', 'Consistent discrepancy from corresponding weeks'],
        signalNote: 'The distortion is interval based, not headcount based.',
        drivers: ['Application of uniform shift blocks on non uniform demand', 'Rigid coverage templates', 'Overlap stacking in periods of low density', 'Evenly applied static buffer instead of density weighted', 'No Redistribution modeling at interval resolution'],
        driverNote: 'Coverage exists. It is simply not positioned where pressure concentrates.',
        inputs: ['Planned versus actual schedule export', 'Interval level coverage distribution', 'Interval level arrival distribution', 'SLA and ASA logs', 'Optional shrinkage by interval'],
        inputNote: 'All data exportable from standard systems.',
        method: ['Reconstruct demand to coverage overlay', 'Calculate coverage compression coefficient', 'Identify over and under distribution bands', 'Model exposure weighted SLA sensitivity', 'Simulate redistribution under constant headcount', 'Map fragility intervals'],
        methodNote: 'This distinguishes the problem of structural misdistribution from the problem of staffing shortfall',
        artifacts: 'Coverage Alignment Index Report',
        artifactItems: ['Coverage misalignment score', 'Compression band heatmap', 'Peak vulnerability intervals', 'Redistribution impact projection', 'Elasticity improvement model'],
        engagement: 'The diagnostic session clarifies whether instability stems from:\n\n• Coverage distribution distortion\n• Or demand shape misalignment\n\nEnables structural redistribution discussion without defaulting to hiring expansion.',
      },
      {
        num: 5,
        name: 'Shift Boundary Vulnerability',
        scenario: 'A SLA degradation always manifests itself in the shift transition hours as in early morning or late afternoon. Stable headcount and stable volume but performance dips align with clock transitions rather than demand spikes. Operational narrative: instability declared in terms of handover friction. Interval data indicates underlying structural insecurity.',
        signals: ['SLA dips coincided with the beginning and closing of the shift.', 'Temporary coverage gaps during transition periods', 'Break stacking at the boundaries of a shift', 'During overlap transitions there is a rise in queue depth.', 'Recovery lag goes beyond the transition window.'],
        signalNote: 'Schedule architecture can be destabilizing.',
        drivers: ['Not enough overlap buffer between shifts', 'Transition inefficiency not modeled in planning', 'Break allocation close to boundaries', 'Rigid shift templates without elasticity', 'No boundary stress modeling in workforce design'],
        driverNote: 'Exposure is incorporated into the schedule design.',
        inputs: ['Shift start and end schedules', 'Break allocation logs', 'Interval level coverage', 'SLA and queue depth logs', 'Optional adherence data during transitions'],
        inputNote: 'All export based only.',
        method: ['Identify boundary intervals', 'Quantify coverage decreases', 'Model transition elasticity', 'Analyze break clustering density', 'Simulate alternative overlap scenarios', 'Map exposure weighted instability'],
        methodNote: 'This isolates structural transition fragility.',
        artifacts: 'Shift Transition Vulnerability Assessment',
        artifactItems: ['Boundary fragility index', 'Overlap adequacy score', 'Transition exposure gradient', 'Break clustering density map', 'Elasticity improvement simulation'],
        engagement: 'Clarifies whether instability is:\n\n• Transition driven\n• Or demand driven\n\nSupports discussion of shift redesign based on quantification of vulnerability instead of anecdotal evidence.',
      },
      {
        num: 6,
        name: 'Skill Allocation Rigidity',
        scenario: 'In a multi queue case, one queue is repeatedly in overload and another one remains idle capacity. Total staffing remains stable, overall utilization acceptable. Queue imbalance persists week after week yet leadership attributes it to volume mix. Data indicates strong structural skill rigidity.',
        signals: ['Differences in use between queues', 'Regular recurring bottleneck intervals', 'Idle capacity concentrated on non critical skill pools.', 'SLA instability localized to high demand skills', 'Repeated imbalance when similar load cases occurs'],
        signalNote: 'Imbalance structural rather than episodic.',
        drivers: ['Skill siloing', 'Certification imbalance', 'Schedule allocation by skill without elasticity modeling', 'Limited cross training depth', 'Inflexible skill coverage templates', 'Queue priority weighting not aligned with staffing architecture'],
        driverNote: 'Rigidity amplifies volatility.',
        inputs: ['Agent to skill mapping', 'Coverage by skill and interval', 'Queue level arrival distribution', 'SLA by queue', 'Optional proficiency tiers'],
        inputNote: 'All exportable from standard WFM systems.',
        method: ['Model queue utilization disparity', 'Derive bottleneck detection coefficient', 'Simulate cross skill elasticity', 'Run redistribution scenarios within current skill capacity', 'Map structural vulnerability by skill cluster'],
        methodNote: 'Separates skill rigidity from macro demand imbalance.',
        artifacts: 'Skill Elasticity & Bottleneck Report',
        artifactItems: ['Bottleneck intensity index', 'Cross skill elasticity curve', 'Redistribution impact simulation', 'Structural vulnerability map by skill', 'Queue level exposure gradient'],
        engagement: 'Diagnostic session clarifies whether instability arises from:\n\n• Volume mix\n• Skill rigidity\n• Structural misallocation\n\nSupports strategic cross training or schedule redesign decision making based on modeled exposure rather than having to rely solely upon judgment.',
      },
    ],
  },
  {
    num: 'III',
    title: 'Intraday Volatility & Queue Dynamics',
    subtitle: 'Structural Instability at Interval Resolution',
    intro: [
      'Intraday instability is rarely random.',
      'Once the same intervals have failed several times, randomness can no longer be the explanation.',
      'In most enterprise operations, intraday volatility concentrates around structural thresholds. Demand density, coverage compression, and queue sensitivity interact in predictable ways.',
      'This domain isolates interval level fragility that hides behind daily aggregates.',
    ],
    patterns: [
      {
        num: 7,
        name: 'Recurring Stress Interval Concentration',
        scenario: 'An operation with 200+ FTE routinely displays SLA breaches during specific intervals and these repetitions occur again from week to week. Demand is stable on a daily basis, forecast accuracy within tolerance, and no changes in headcount. Operations describes the burn intervals "unavoidable". Interval reconstruction reveals recurrence of structure.',
        signals: ['SLA degradation was concentrated in fixed intervals', 'Abandon spikes aligned with the same intervals', 'Queue depth increases predictably during these intervals', 'High repetition on similar days of the week', 'Recovery slope is maintained after the stress zone'],
        signalNote: 'This is a structural interval fragility, not episodic variance.',
        drivers: ['Density compression interacting with coverage misalignment', 'Threshold sensitivity near critical occupancy levels', 'Micro adherence clustering during peak pressure', 'Missing staffing elasticity at interval level', 'Boundary effects magnifying sensitivity of the queue'],
        driverNote: 'Fragility embedded in interval geometry.',
        inputs: ['Arrival data: 6-8 weeks', 'Interval level coverage distribution', 'SLA and ASA logs', 'Queue depth logs', 'Optional adherence logs'],
        inputNote: 'All export based.',
        method: ['Detect stress interval clustering', 'Model recurrence probability', 'Quantify exposure weighted SLA impact', 'Model queue depth elasticity', 'Simulate adjusted coverage scenarios', 'Map interval fragility gradient'],
        methodNote: 'Separates structural recurrence from perceived randomness.',
        artifacts: 'Stress Interval Distribution Matrix',
        artifactItems: ['Recurrence probability score', 'Peak exposure gradient', 'Elasticity stress curve', 'Structural vulnerability map', 'Redistribution impact projection'],
        engagement: 'Diagnostic session clarifies whether instability stems from:\n\n• Coverage distribution distortion\n• Demand density compression\n• Behavioral clustering\n• Threshold sensitivity\n\nSupports targeted structural intervention rather than reactive firefighting.',
      },
      {
        num: 8,
        name: 'Queue Elasticity Breakdown',
        scenario: 'Small changes in arrival rate induce a nonlinear SLA collapse and the occupancy rises by a few percentage points. ASA increases sharply. Headcount unchanged. OPS attribute instability to unexpected load sensitivity but interval analysis reveals structural elasticity limits.',
        signals: ['Nonlinear SLA responses to incremental changes in arrival', 'Queue depth rises sharply above threshold', 'Elasticity threshold lower than assumed', 'Recovery lag unrelated to size of disturbance', 'AHT variance amplifies performance collapse'],
        signalNote: 'The system is being operated at near its threshold of sensitivity range.',
        drivers: ['Staffing positioned too close to critical occupancy threshold', 'No explicit elasticity modeling in planning logic', 'Static buffer allocation', 'High AHT variance sensitivity', 'Queue configuration amplifying pressure'],
        driverNote: 'Fragility embedded in threshold design.',
        inputs: ['Arrival rate by interval', 'Queue depth logs', 'Interval coverage', 'AHT distribution', 'SLA logs'],
        inputNote: 'All exportable from standard systems.',
        method: ['Derive elasticity curve between arrival rate and SLA', 'Compute occupancy to SLA curve', 'Identify threshold sensitivity band', 'Simulate moderate variance stress scenarios', 'Map structural fragility zone'],
        methodNote: 'Isolates structural sensitivity rather than blaming operational variance.',
        artifacts: 'Queue Elasticity Sensitivity Model',
        artifactItems: ['Elasticity curve coefficient', 'Threshold exposure index', 'Amplification factor', 'Structural fragility band', 'Elasticity improvement projection'],
        engagement: 'Explains why small shifts in demand lead to big swings in performance.\n\nPermits structural rebalancing decisions without relying on inflation of the headcount.',
      },
    ],
  },
  {
    num: 'IV',
    title: 'Workforce Behavior & Execution Variance',
    subtitle: 'Structural Amplification of Human Factors',
    intro: [
      'Behavioral variance becomes structurally relevant when it aligns with peak demand exposure.',
      'The majority of enterprise environments present an acceptable overall level of adherence and shrinkage.',
      'Instability develops when small deviations concentrate within intervals of high pressure.',
      'This domain isolates the structural amplification of normal human variance.',
      'The goal is not to assign blame. The aim is to understand impact concentration.',
    ],
    patterns: [
      {
        num: 9,
        name: 'Adherence Impact Distortion',
        scenario: 'Operation consistently reports adherence in range of 92-95 percent. Absence static with no significant decrease in staff. Despite this, SLA instability concentrates in peak intervals. Intraday report notes a slight drift. Interval level reconstruction highlights increased structural exposure.',
        signals: ['Global adherence within acceptable range', 'Interval level deviation clustering in high density windows', 'Micro variances accumulate during peak load', 'SLA sensitivity significantly higher during clustered drift', 'Aggregate adherence masks exposure concentration'],
        signalNote: 'Impact concentrated, not adherence collapsing.',
        drivers: ['Behavioral synchronization during peak fatigue windows', 'Informal break alignment during pressure intervals', 'Incentives tied to aggregate adherence rather than peak stability', 'Supervisory cadence not weighted toward interval sensitivity', 'Queue threshold proximity amplifying minor deviation'],
        driverNote: 'Small deviations amplify when aligned with structural pressure.',
        inputs: ['Interval adherence logs', 'Planned schedule state allocation', 'Interval arrival distribution', 'SLA and ASA logs', 'Optional shrinkage categorization'],
        inputNote: 'All obtainable via export.',
        method: ['Reconstruct interval level deviation density curve', 'Compute impact weighted adherence metric', 'Correlate deviation clustering with SLA degradation', 'Separate structural amplification from individual variance', 'Simulate normalized adherence under peak exposure'],
        methodNote: 'Isolates structural impact rather than focusing on individual performance.',
        artifacts: 'Impact Weighted Adherence Distribution Report',
        artifactItems: ['Interval deviation density map', 'Peak exposure amplification index', 'Behavioral clustering coefficient', 'Structural sensitivity overlay'],
        engagement: 'Diagnostic session clarifies whether instability is driven by:\n\n• Behavioral clustering\n• Coverage misdistribution\n• Queue threshold sensitivity\n\nProvides leadership structural understanding without defaulting to disciplinary interpretation.',
      },
      {
        num: 10,
        name: 'Behavioral Break Synchronization Clustering',
        scenario: 'SLA degradation recurs during intervals where breaks and avail states appear clustered. Aggregate adherence remains acceptable. No formal break violation report exists, but performance instability persists.',
        signals: ['Break clustering density exceeds random distribution expectation', 'Offphone states correlate with micro peaks', 'Performance degradation aligns with behavioral synchronization windows', 'Pattern consistent across multiple weeks'],
        signalNote: 'Clustering amplifies structural exposure.',
        drivers: ['Informal peer synchronization', 'Break allocation insufficiently staggered', 'Cultural tolerance for flexibility during peak windows', 'Missing interval weighted adherence modeling', 'Limited visibility into clustering impact'],
        driverNote: 'Normal human behavior intersects with structural sensitivity.',
        inputs: ['Interval state logs', 'Break allocation schedules', 'Arrival density curve', 'SLA logs', 'Optional supervisor coverage allocation'],
        inputNote: 'Export based analysis.',
        method: ['Reconstruct behavioral density distribution', 'Model expected random baseline', 'Compute clustering deviation coefficient', 'Map performance amplification', 'Simulate staggered allocation scenarios'],
        methodNote: 'Separates structural clustering from misconduct narratives.',
        artifacts: 'Behavioral Density & Synchronization Analysis',
        artifactItems: ['Clustering coefficient', 'Break density heatmap', 'Performance amplification gradient', 'Structural impact projection under redistribution'],
        engagement: 'Helps leadership determine whether instability requires:\n\n• Break staggering redesign\n• Supervisory cadence adjustment\n• Or structural schedule modification\n\nGrounded in measured exposure rather than anecdote.',
      },
    ],
  },
  {
    num: 'V',
    title: 'Cost Structure & Capacity Efficiency',
    subtitle: 'Structural Cost Exposure & Distribution Inefficiency',
    intro: [
      'Cost instability rarely begins with excessive hiring.',
      'More often, it results from how capacity was allocated against demand uncertainty.',
      'When the paid hours, productive distribution and arrival density are mismatched, CPC increases even though volume and headcount are held constant.',
      'This domain isolates structural cost exposure embedded in coverage design and volatility sensitivity.',
      'Our focus is structural efficiency and not superficial cost cutting.',
    ],
    patterns: [
      {
        num: 11,
        name: 'Idle Interval Accumulation',
        scenario: 'Cost per contact increases with cost per head and headcount remaining flat. Utilization reports look fine at an aggregate level. No visible operational failure but financial pressure increases without obvious staffing expansion. Interval analysis shows clustering of unused capacity.',
        signals: ['Idle capacity clustered in low density intervals', 'Paid hours stable but productive distribution uneven', 'Utilization variance high across intervals', 'Idle time not evenly distributed', 'CPC inflation correlates with distribution imbalance'],
        signalNote: 'The distortion lies in distribution inefficiency, not overstaffing.',
        drivers: ['Coverage blocks misaligned with demand density', 'Excess overlap during low demand windows', 'Uniform buffer logic applied without volatility weighting', 'Fragmented shift design', 'Missing interval level redistribution modeling'],
        driverNote: 'Capacity exists but is inefficiently positioned.',
        inputs: ['Paid versus productive hours by interval', 'Coverage distribution', 'Arrival distribution', 'Utilization logs', 'Cost per contact trends'],
        inputNote: 'All exportable from standard systems.',
        method: ['Quantify idle intervals at resolution level', 'Model utilization variance distribution', 'Overlay coverage against demand density', 'Derive structural inefficiency gradient', 'Simulate redistribution under constant headcount'],
        methodNote: 'Separates structural inefficiency from perceived overstaffing.',
        artifacts: 'Idle Capacity Exposure Analysis',
        artifactItems: ['Idle cost ratio', 'Interval level inefficiency heatmap', 'Coverage inefficiency coefficient', 'Redistribution impact simulation', 'Structural efficiency index'],
        engagement: 'Clarifies whether rising CPC is driven by:\n\n• Structural distribution inefficiency\n• Or macro staffing imbalance\n\nSupports cost stabilization discussion grounded in structural data.',
      },
      {
        num: 12,
        name: 'Volatility Induced Capacity Cost Distortion',
        scenario: 'Cost per contact varies non linearly under moderate variations in demand. Headcount unchanged. Volume steady. Still CPC sensitivity is high. Leadership attributes the variation to external conditions. Structural modeling indicates an embedded volatility tax.',
        signals: ['CPC sensitivity high in comparison with moderate volatility shifts', 'Coverage elasticity insufficient under variability', 'Paid hours rigid relative to demand movement', 'Cost curve displays non linear behavior'],
        signalNote: 'System cost response is greater than demand fluctuation.',
        drivers: ['Rigid shift patterns', 'Static buffer design', 'Planning logic does not model elasticity', 'AHT variance sensitivity not stress tested', 'No percentile based cost modeling'],
        driverNote: 'The cost structure presumes a stability that demand doesn\u2019t guarantee.',
        inputs: ['CPC by interval or day', 'Coverage distribution', 'Arrival distribution', 'AHT variance curve', 'Paid vs productive hours'],
        inputNote: 'Export based sufficient.',
        method: ['Model volatility elasticity curve', 'Calculate CPC sensitivity slope', 'Estimate structural capacity mispricing', 'Stress test cost exposure under volatility scenarios', 'Decompose structural cost drivers'],
        methodNote: 'Separates volatility caused cost exposure from operational inefficiency.',
        artifacts: 'Capacity Cost Decomposition Report',
        artifactItems: ['Volatility elasticity coefficient', 'CPC sensitivity index', 'Structural mispricing estimate', 'Risk adjusted cost exposure band', 'Elasticity correction projection'],
        engagement: 'Differentiates structural cost volatility from external volatility.\n\nSupports executive level cost strategy based on structural modeling and not reactive cost cutting.',
      },
    ],
  },
];

const SECTION_LABELS = [
  { key: 'scenario', label: 'Observed Scenario' },
  { key: 'signals', label: 'Signal Structure', list: true, noteKey: 'signalNote' },
  { key: 'drivers', label: 'Structural Drivers', list: true, noteKey: 'driverNote' },
  { key: 'inputs', label: 'Data Inputs', list: true, noteKey: 'inputNote' },
  { key: 'method', label: 'Analytical Method', list: true, noteKey: 'methodNote' },
  { key: 'artifacts', label: 'Deliverable Artifact', artifactItems: true },
  { key: 'engagement', label: 'Engagement Path & Impact' },
];




/* ── Atlas rail — hierarchical (DOMAIN → pattern) + a misclassifications leaf.
   Rail ids ARE the DOM anchors (atlas-domain-1..12 + atlas-misclassifications).
   Domain group labels are concise nav-only aliases; pattern labels are the
   existing pattern names, verbatim. Body headings remain untouched. ── */

const DOMAIN_RAIL_LABELS = {
  I: 'Demand & Forecast',
  II: 'Coverage & Schedule',
  III: 'Intraday & Queue',
  IV: 'Behavior & Execution',
  V: 'Cost & Capacity',
};

const ATLAS_RAIL = [
  ...ATLAS_DOMAINS.map((domain) => ({
    label: `Domain ${domain.num} · ${DOMAIN_RAIL_LABELS[domain.num]}`,
    children: domain.patterns.map((pattern) => ({
      id: `atlas-domain-${pattern.num}`,
      num: String(pattern.num).padStart(2, '0'),
      label: pattern.name,
    })),
  })),
  { id: 'atlas-misclassifications', num: '13', label: 'Common Executive Misclassifications' },
];

const ATLAS_IDS = ATLAS_RAIL.flatMap((item) =>
  item.children ? item.children.map((c) => c.id) : [item.id]
);

/* ── A5 controlled variation — two lg-only treatments alternated across the
   12 pattern blocks. Content order inside a pattern is never changed; below
   lg both collapse to the unchanged single column. ── */

const SPLIT_LEFT_SECTIONS = new Set(['scenario', 'signals']);
const isSplitTreatment = (num) => num % 2 === 1;

/* One Atlas section (Observed Scenario / Signal Structure / … / Engagement).
   Same markup as the original single-column panel. */
function renderPatternSection(section, pattern) {
  const { key, label, list, noteKey, artifactItems } = section;
  return (
    <div key={key}>
      <h4 className="text-xs font-mono tracking-widest uppercase text-zinc-500 mb-3">{label}</h4>
      {key === 'scenario' && (
        <div className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line">
          {pattern.scenario}
        </div>
      )}
      {key === 'engagement' && (
        <div className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line">
          {pattern.engagement}
        </div>
      )}
      {list && pattern[key] && (
        <>
          <ul className="space-y-2 mb-3">
            {pattern[key].map((item, idx) => (
              <li key={idx} className="flex items-start gap-2.5">
                <span className="text-memo-muted mt-0.5 flex-shrink-0" aria-hidden="true">—</span>
                <span className="text-sm text-zinc-400 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          {noteKey && pattern[noteKey] && (
            <p className="text-sm text-memo-muted italic border-l-2 border-memo-line pl-3">{pattern[noteKey]}</p>
          )}
        </>
      )}
      {artifactItems && (
        <>
          <p className="text-sm text-zinc-300 font-medium mb-3">{pattern.artifacts}</p>
          {pattern.artifactItems && (
            <>
              <p className="text-xs text-zinc-500 mb-2">Includes:</p>
              <ul className="space-y-1.5">
                {pattern.artifactItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <span className="text-memo-muted mt-0.5 flex-shrink-0" aria-hidden="true">—</span>
                    <span className="text-sm text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* Treatment (a) — split layout: scenario/signals left, structural drivers
   right (hairline-ruled), remaining sections full width below. */
function renderSplitPattern(pattern) {
  return (
    <div className="space-y-8">
      <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-12">
        <div className="space-y-8 min-w-0">
          {SECTION_LABELS
            .filter((s) => SPLIT_LEFT_SECTIONS.has(s.key))
            .map((s) => renderPatternSection(s, pattern))}
        </div>
        <div className="space-y-8 min-w-0 lg:border-l lg:border-memo-line lg:pl-6">
          {SECTION_LABELS
            .filter((s) => s.key === 'drivers')
            .map((s) => renderPatternSection(s, pattern))}
        </div>
      </div>
      <div className="space-y-8">
        {SECTION_LABELS
          .filter((s) => !SPLIT_LEFT_SECTIONS.has(s.key) && s.key !== 'drivers')
          .map((s) => renderPatternSection(s, pattern))}
      </div>
    </div>
  );
}

/* Treatment (b) — Deliverable Artifact promoted to a hairline exhibit plate
   (bordered, labeled); remaining sections stay single column. */
function renderPlatePattern(pattern) {
  return (
    <div className="space-y-8">
      {SECTION_LABELS.map((section) => {
        if (!section.artifactItems) return renderPatternSection(section, pattern);
        return (
          <div key="plate-artifact" className="lg:border lg:border-memo-line-2 lg:rounded lg:p-5">
            {renderPatternSection(section, pattern)}
          </div>
        );
      })}
    </div>
  );
}

export function Atlas() {
  const [expandedPattern, setExpandedPattern] = useState(null);
  const toggle = (id) => setExpandedPattern(expandedPattern === id ? null : id);
  // Scroll-spy over the 13 section ids (module-level constant array).
  const activeId = useSectionSpy(ATLAS_IDS, '0px 0px -80% 0px');

  return (
    <div className="bg-zinc-950">
      <SEOHead />
      {/* NOTE: no overflow-hidden on the section — it would trap the rail's position:sticky. */}
      <section className="relative">
        {/* ── Compact masthead (replaces the oversized hero) ── */}
        <PageMasthead
          wide
          field="page"
          fieldFocal="0.68,0.40"
          eyebrow="Instability Classification"
          title="OnPoint Instability Atlas"
          intro="Structural Decomposition of Workforce System Instability"
        />
        <div className="relative z-10">

          {/* ── Shared spine — persistent left nav (lg) + mobile section nav (<lg) ── */}
          <div className="container-site spine-shell lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
            <PageNav items={ATLAS_RAIL} activeId={activeId} label="Atlas sections" />

            <div className="lg:min-w-0 lg:border-l lg:border-memo-line lg:pl-10">
              <MobileSectionNav items={ATLAS_RAIL.flatMap((i) => (i.children ? i.children : [i]))} activeId={activeId} label="Atlas sections" />

              {/* ── On this Atlas — why / how / scope + editorial, preserved
                   verbatim; collapsed by default so the spine starts at the
                   masthead. ── */}
              <details className="group mb-10 border border-zinc-800 bg-zinc-900/20">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4 select-none [&::-webkit-details-marker]:hidden">
                  <span className="text-sm font-heading font-medium text-zinc-200">Why This Atlas Exists</span>
                  <ChevronDown size={15} className="text-zinc-500 flex-shrink-0 transition-transform duration-300 group-open:rotate-180" aria-hidden="true" />
                </summary>

                <div className="border-t border-zinc-800 px-5 pt-5 pb-5">
                  <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] lg:gap-12 items-start">
              {/* Editorial — the contradiction at interval level */}
              <div className="min-w-0 space-y-3">
                <p className="text-base text-zinc-400 leading-relaxed">
                  You know where workforce instability usually doesn\u2019t start? Where leaders think it does.
                </p>
                <div className="space-y-1">
                  <p className="text-sm text-zinc-500">It\u2019s not usually about overall volume.</p>
                  <p className="text-sm text-zinc-500">It isn\u2019t about headcount alone.</p>
                  <p className="text-sm text-zinc-500">It\u2019s almost never tooling.</p>
                </div>
                <p className="text-base text-zinc-400 leading-relaxed">
                  Instability emerges when distribution, coverage architecture and human behavior misalign under operational pressure.
                </p>
                <p className="text-base text-zinc-400 leading-relaxed">
                  In large CX environments, we repeatedly observe the same contradiction:
                </p>
                <div className="space-y-1 pl-4 border-l-2 border-memo-line">
                  <p className="text-sm text-zinc-500">Forecasting accuracy appears to be good.</p>
                  <p className="text-sm text-zinc-500">Staffing totals match requirement.</p>
                  <p className="text-sm text-zinc-500">Adherence sits within tolerance.</p>
                </div>
                <p className="text-base text-zinc-400 leading-relaxed">
                  But performance degrades in predictable and quantifiable ways
                </p>
                <p className="text-base text-zinc-300 leading-relaxed font-medium">
                  This is not a problem of aggregate numbers but a problem of structural balance.
                </p>
                <p className="text-base text-zinc-400 leading-relaxed">
                  OnPoint Instability Atlas: mapping persistent instability patterns across enterprise workforce systems via interval level data and structural decomposition.
                </p>
                <p className="text-base text-amber-500 leading-relaxed">
                  Our interpretation is limited to what the submitted operating data supports.
We do not provide operational instructions, performance assurances or outcome guarantees.
                </p>
              </div>

              {/* Guide column — how the Atlas is organized and scoped */}
              <div className="min-w-0">
                <div className="pb-5">
                  <div className="space-y-2">
                    <p className="text-sm text-zinc-400 leading-relaxed">It\u2019s the breakdowns we call the “noise”.</p>
                    <div className="space-y-1">
                      <p className="text-sm text-zinc-500">Volume fluctuation.</p>
                      <p className="text-sm text-zinc-500">The inherent volatility.</p>
                      <p className="text-sm text-zinc-500">Slight adherence drift.</p>
                      <p className="text-sm text-zinc-500">Just a terrible day. Again and again.</p>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed">It\u2019s not so plausible that it\u2019s just random when the same intervals keep failing.</p>
                    <p className="text-sm text-zinc-400 leading-relaxed">This Atlas separates:</p>
                    <ul className="space-y-1.5 pl-4">
                      {['Structural deformation', 'Distribution mismatch', 'Behavioral amplification', 'Threshold sensitivity', 'Cost exposure'].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="text-memo-signal mt-0.5 flex-shrink-0" aria-hidden="true">—</span>
                          <span className="text-sm text-zinc-400">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm text-zinc-400 leading-relaxed">This is not an essay discussing concepts.</p>
                    <p className="text-sm text-zinc-400 leading-relaxed">It is a working map derived from enterprise workforce systems operating under real pressure.</p>
                  </div>
                </div>

                <div className="border-t border-memo-line pt-5 pb-5">
                  <h3 className="font-heading text-lg font-medium text-zinc-200 mb-3">How to Use This Atlas</h3>
                  <div className="space-y-2">
                    <p className="text-sm text-zinc-400 leading-relaxed">Each domain isolates a structural instability layer:</p>
                    <ul className="space-y-1.5 pl-4">
                      {['Demand shape', 'Coverage distribution', 'Intraday stress dynamics', 'Workforce behavior variance', 'Cost elasticity and structural exposure'].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="text-memo-signal mt-0.5 flex-shrink-0" aria-hidden="true">—</span>
                          <span className="text-sm text-zinc-400">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm text-zinc-400 leading-relaxed">Within each domain you will find:</p>
                    <div className="space-y-1 pl-4">
                      <p className="text-sm text-zinc-500">The operational contexts you have seen</p>
                      <p className="text-sm text-zinc-500">The signal patterns in your exported data</p>
                      <p className="text-sm text-zinc-500">Structural drivers in the planning logic</p>
                      <p className="text-sm text-zinc-500">Techniques to isolate distortion</p>
                      <p className="text-sm text-zinc-500">Deliverable summaries to be presented to leadership</p>
                    </div>
                    <p className="text-sm text-zinc-100 leading-relaxed">The goal is systematic transparency.</p>
                    <div className="space-y-1">
                      <p className="text-sm text-zinc-500">Not blame.</p>
                      <p className="text-sm text-zinc-500">Not tool replacement.</p>
                      <p className="text-sm text-zinc-500">Not reactive staffing decisions.</p>
                    </div>
                    <p className="text-sm text-amber-500 font-medium">Structural clarity.</p>
                  </div>
                </div>

                <div className="border-t border-memo-line pt-5">
                  <h3 className="font-heading text-lg font-medium text-zinc-100 mb-3">Scope & Method</h3>
                  <div className="space-y-2">
                    <p className="text-sm text-zinc-100 leading-relaxed">All analysis referenced in this Atlas operates on raw WFM data.</p>
                    <p className="text-sm text-zinc-400 leading-relaxed">Typical inputs include:</p>
                    <ul className="space-y-1.5 pl-4">
                      {['Interval arrival logs', 'Schedule and coverage reports', 'SLA and ASA history', 'Forecast files', 'Adherence and AHT variance'].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="text-memo-signal mt-0.5 flex-shrink-0" aria-hidden="true">—</span>
                          <span className="text-sm text-zinc-400">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm text-amber-500 leading-relaxed">If structural instability is present, it will be reflected in operating data.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytical evidence layer — interval residual figure (illustrative of the
                method named in the locked copy; not fabricated client metrics) */}
            <div className="mt-10">
              <FigureIntervalResidual />
            </div>
          </div>
        </details>
              {/* Domains and Patterns */}
              <div className="space-y-16">
                {ATLAS_DOMAINS.map((domain) => (
                  <div key={domain.num} id={`domain-${domain.num}`} className="first:pt-0 pt-10">
                    {/* Domain header */}
                    <div className="mb-8 pb-6 border-b border-memo-line">
                      <div className="flex items-baseline gap-4 mb-2">
                        <span className="text-xs font-mono tracking-widest text-amber-500">DOMAIN {domain.num}</span>
                      </div>
                      <h2 className="font-heading text-2xl font-medium text-zinc-100 mb-2">{domain.title}</h2>
                      {domain.subtitle && (
                        <h3 className="font-heading text-lg text-zinc-400 mb-4">{domain.subtitle}</h3>
                      )}
                      {domain.intro && (
                        <div className="space-y-3 max-w-3xl">
                          {domain.intro.map((para, idx) => (
                            <p key={idx} className="text-sm text-zinc-500 leading-relaxed">{para}</p>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Patterns */}
                    <div className="space-y-3">
                      {domain.patterns.map((pattern) => {
                        const id = `atlas-domain-${pattern.num}`;
                        const isOpen = expandedPattern === id;
                        return (
                          <div
                            key={id}
                            id={id}
                            data-testid={`atlas-pattern-${pattern.num}`}
                            className="border-b border-memo-line transition-colors duration-200"
                          >
                            <button
                              data-testid={`atlas-pattern-toggle-${pattern.num}`}
                              aria-expanded={isOpen}
                              className="w-full text-left py-4 flex items-center justify-between gap-4 group"
                              onClick={() => toggle(id)}
                            >
                              <div className="flex items-center gap-4 min-w-0">
                                <span className="text-xs font-mono text-amber-500 flex-shrink-0">
                                  {String(pattern.num).padStart(2, '0')}
                                </span>
                                <span className="font-heading text-sm font-medium text-zinc-200">{pattern.name}</span>
                              </div>
                              {isOpen ? <ChevronUp size={15} className="text-zinc-500 flex-shrink-0" /> : <ChevronDown size={15} className="text-zinc-500 flex-shrink-0" />}
                            </button>

                            {isOpen && (
                              <div className="border-t border-memo-line p-6">
                                {isSplitTreatment(pattern.num)
                                  ? renderSplitPattern(pattern)
                                  : renderPlatePattern(pattern)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Common Misclassifications */}
              <div id="atlas-misclassifications" className="mt-16 border-b border-memo-line pb-8">
                <h2 className="font-heading text-xl font-medium text-zinc-100 mb-2">
                  Common Executive Misclassifications
                </h2>
                <p className="text-sm text-zinc-500 mb-6">And Why They Persist</p>
                <div className="space-y-4">
                  {[
                    { label: 'Headcount shortage', actual: 'Distribution failure is labeled as staffing shortage: Total head count is generally okay but interval distribution is not.' },
                    { label: 'Forecast error', actual: 'Threshold fragility is often attributed to forecast error. Instability often occurs within normal tolerance thresholds, not only during extreme peaks.' },
                    { label: 'Random adherence drift', actual: 'Behavioral clustering is often dismissed as random variation. In reality: clustering intensifies under peak demand.' },
                    { label: 'Demand growth', actual: 'Volatility acts as a structural cost tax. CPC increases independently of volume growth when distribution inefficiency amplifies cost sensitivity.' },
                    { label: 'Tool limitations', actual: 'Platform capability is often blamed without structural evidence. In most cases the constraint lies in model assumptions or architectural rigidity.' },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="grid grid-cols-[220px_1fr] gap-6 py-4 border-b border-memo-line last:border-0"
                    >
                      <div className="flex-shrink-0">
                        <span className="text-xs font-mono uppercase tracking-[0.14em] text-memo-warn inline-flex flex-col leading-tight">
                        <span className="text-memo-warn text-[15px] tracking-widest uppercase">
                          Misclassified as:
                        </span>
                        <span className="text-memo-warn">
                          {item.label}
                          </span>
                          </span>
                      </div>
                      <p className="text-sm text-zinc-400 leading-relaxed">{item.actual}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-memo-line">
                  <p className="text-sm text-zinc-500 mb-4">
                    If you recognize your pattern, pause before intervening.
                  </p>
                  <Link
                    to="/intake"
                    data-testid="atlas-intake-cta"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-memo-ink text-memo-paper text-sm font-medium hover:bg-memo-signal transition-colors duration-200"
                  >
                    Begin Intake <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

